import { useEffect, useState } from 'react';
import { FileText, BadgeCheck, Calendar, Coins } from 'lucide-react';
import { client } from '../../lib/sanity';
import type { FundingProject } from '../../types/sanity';

export function FundingProjectCard() {
  const [projects, setProjects] = useState<FundingProject[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const result = await client.fetch<FundingProject[]>(`
          *[_type == "fundingProject" && isPublic == true && !(_id in path("drafts.**"))] | order(coalesce(orderRank, 999) asc, _createdAt desc) {
            _id,
            title,
            status,
            financier,
            fundingPeriod,
            amount,
            description,
            activities,
            outcomes,
            note,
            "attachments": attachments[]{
              "label": label,
              "url": asset->url
            }
          }
        `);

        setProjects(result);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching funding projects:', err);
        setError('Nie udało się załadować projektów dofinansowania');
        setLoading(false);
      }
    };

    fetchProjects();
  }, []);

  if (loading) {
    return (
      <section className="bg-white dark:bg-accessible-gray-800 high-contrast:bg-black p-8 rounded-lg shadow-lg">
        <div className="flex items-center gap-4 mb-6">
          <div className="w-12 h-12 rounded-full bg-accessible-red-600/10 flex items-center justify-center">
            <Coins className="w-6 h-6 text-accessible-red-600" aria-hidden="true" />
          </div>
          <div>
            <h2 className="card-title">Projekty dofinansowane</h2>
          </div>
        </div>
        <p className="body-medium text-accessible-gray-600 dark:text-white/90 high-contrast:text-white">
          Ładowanie projektów...
        </p>
      </section>
    );
  }

  if (error || projects.length === 0) {
    return null; // Don't show anything if there's an error or no projects
  }

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'current':
        return 'W realizacji';
      case 'completed':
        return 'Zakończony';
      case 'planned':
        return 'Planowany';
      default:
        return status;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'current':
        return 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200 border-green-300 dark:border-green-700';
      case 'completed':
        return 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200 border-blue-300 dark:border-blue-700';
      case 'planned':
        return 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-200 border-yellow-300 dark:border-yellow-700';
      default:
        return 'bg-accessible-gray-100 dark:bg-accessible-gray-800 text-accessible-gray-800 dark:text-accessible-gray-200';
    }
  };

  const formatAmount = (amount: number) => {
    return new Intl.NumberFormat('pl-PL', {
      style: 'currency',
      currency: 'PLN',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  return (
    <section className="bg-white dark:bg-accessible-gray-800 high-contrast:bg-black p-8 rounded-lg shadow-lg">
      <div className="flex items-center gap-4 mb-6">
        <div className="w-12 h-12 rounded-full bg-accessible-red-600/10 flex items-center justify-center">
          <Coins className="w-6 h-6 text-accessible-red-600" aria-hidden="true" />
        </div>
        <div>
          <h2 className="card-title">Projekty dofinansowane</h2>
          <p className="body-medium text-accessible-gray-700 dark:text-white/90 high-contrast:text-white">
            Fundacja Znajdki realizuje projekty wspierane przez instytucje i partnerów
          </p>
        </div>
      </div>

      <div className="space-y-6">
        {projects.map((project) => (
          <article
            key={project._id}
            className="border border-accessible-gray-200 dark:border-accessible-gray-700 high-contrast:border-black rounded-lg p-6 space-y-4 bg-accessible-gray-50/50 dark:bg-accessible-gray-900/30 high-contrast:bg-white"
          >
            {/* Header */}
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div className="flex-1 min-w-0">
                <h3 className="text-xl font-bold text-accessible-gray-900 dark:text-white high-contrast:!text-black mb-2">
                  {project.title}
                </h3>
                <p className="body-medium text-accessible-gray-700 dark:text-white/90 high-contrast:!text-black">
                  {project.financier}
                </p>
              </div>
              
              <span
                className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-semibold border ${getStatusColor(project.status)}`}
              >
                <BadgeCheck className="w-4 h-4" aria-hidden="true" />
                {getStatusLabel(project.status)}
              </span>
            </div>

            {/* Details */}
            <div className="flex flex-wrap gap-4 text-sm">
              <div className="flex items-center gap-2 text-accessible-gray-700 dark:text-white/90 high-contrast:text-black">
                <Calendar className="w-4 h-4" aria-hidden="true" />
                <span>{project.fundingPeriod}</span>
              </div>
              
              {project.amount && (
                <div className="flex items-center gap-2 text-accessible-gray-700 dark:text-white/90 high-contrast:text-black font-semibold">
                  <Coins className="w-4 h-4" aria-hidden="true" />
                  <span>{formatAmount(project.amount)}</span>
                </div>
              )}
            </div>

            {/* Description */}
            <p className="body-medium text-accessible-gray-800 dark:text-white high-contrast:!text-black">
              {project.description}
            </p>

            {/* Activities */}
            {project.activities && project.activities.length > 0 && (
              <div className="space-y-2">
                <h4 className="body-medium font-semibold text-accessible-gray-900 dark:text-white high-contrast:!text-black">
                  Najważniejsze działania:
                </h4>
                <ul className="list-disc list-inside space-y-1 body-small text-accessible-gray-700 dark:text-white/90 high-contrast:!text-black ml-2">
                  {project.activities.map((activity, index) => (
                    <li key={index}>{activity}</li>
                  ))}
                </ul>
              </div>
            )}

            {/* Outcomes */}
            {project.outcomes && project.outcomes.length > 0 && (
              <div className="space-y-2">
                <h4 className="body-medium font-semibold text-accessible-gray-900 dark:text-white high-contrast:!text-black">
                  Rezultaty / osiągnięcia:
                </h4>
                <ul className="list-disc list-inside space-y-1 body-small text-accessible-gray-700 dark:text-white/90 high-contrast:!text-black ml-2">
                  {project.outcomes.map((outcome, index) => (
                    <li key={index}>{outcome}</li>
                  ))}
                </ul>
              </div>
            )}

            {/* Note */}
            {project.note && (
              <div className="bg-accessible-red-50/50 dark:bg-accessible-red-900/10 high-contrast:bg-white border border-accessible-red-200 dark:border-accessible-red-800 high-contrast:border-black rounded-md p-4">
                <p className="body-small text-accessible-gray-800 dark:text-white high-contrast:!text-black">
                  {project.note}
                </p>
              </div>
            )}

            {/* Attachments */}
            {project.attachments && project.attachments.length > 0 && (
              <div className="space-y-2">
                <h4 className="body-medium font-semibold text-accessible-gray-900 dark:text-white high-contrast:!text-black">
                  Załączniki:
                </h4>
                <div className="flex flex-wrap gap-2">
                  {project.attachments.map((attachment, index) => (
                    <a
                      key={index}
                      href={attachment.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 px-3 py-2 rounded-md bg-white dark:bg-accessible-gray-800 border border-accessible-gray-300 dark:border-accessible-gray-600 text-accessible-gray-700 dark:text-white/90 high-contrast:text-white hover:bg-accessible-gray-50 dark:hover:bg-accessible-gray-700 body-small font-medium transition-colors"
                    >
                      <FileText className="w-4 h-4" aria-hidden="true" />
                      {attachment.label || `Załącznik ${index + 1}`}
                    </a>
                  ))}
                </div>
              </div>
            )}
          </article>
        ))}
      </div>
    </section>
  );
}

